// Shared pieces of the Applications product (AuthNull as the identity provider
// for a tenant's own apps): app types, client-side copies of the API's rules,
// the shown-once secret dialog, copyable value rows and error handling.
import React, { useEffect, useState } from "react";
import { Button, Input, Modal, message } from "antd";
import { AppWindow, Plus, RotateCcw, ShieldAlert, TriangleAlert, X } from "lucide-react";

import { logOutUser } from "../../common";
import { SquareBadge } from "../../design-system/tableUi";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../design-system/tokens";
import { getIdpSettings, idpFailure, rotateAppSecret, tenantBaseUrl } from "../../service/idpService";
import { CopyButton, card, Note } from "../enduser/ssh-proxy/sshProxyUi";

/* ------------------------------------------------------------------ types */

// What the admin picks. protocol + tokenEndpointAuthMethod is what is saved.
// Ordered by how often each is used: SAML and Web App first, MFA API next,
// single-page last. The default for a new app is still "web" (see appTypeKey).
export const APP_TYPES = [
  {
    key: "saml",
    protocol: "saml",
    title: "SAML app",
    tech: "SAML 2.0",
    desc: "Most enterprise SaaS. E.g. GitLab, Salesforce, Jira, Zoho.",
  },
  {
    key: "web",
    protocol: "oidc",
    method: "client_secret_basic",
    title: "Web App",
    tech: "OpenID Connect",
    desc: "Your own or third-party web app that supports OpenID Connect. E.g. Grafana, internal portals.",
  },
  {
    key: "mfa_api",
    protocol: "mfa_api",
    method: "client_secret_basic",
    title: "MFA API",
    tech: "The app keeps its own login",
    desc: "Your in-house app keeps its own login and asks AuthNull only for the push. E.g. your ERP.",
  },
  {
    key: "spa",
    protocol: "oidc",
    method: "none",
    title: "Single-page or mobile app",
    tech: "OpenID Connect",
    desc: "Runs entirely in the browser or on a phone, with no server of its own.",
  },
];

export const appTypeKey = (app) => {
  if (!app) return "web";
  if (app.protocol === "saml") return "saml";
  if (app.protocol === "mfa_api") return "mfa_api";
  return app.oidc?.tokenEndpointAuthMethod === "none" ? "spa" : "web";
};

export const PROTOCOL_LABEL = { oidc: "OIDC", saml: "SAML", mfa_api: "MFA API" };

export const ProtocolBadge = ({ protocol }) => (
  <SquareBadge tone={protocol === "saml" ? "warn" : protocol === "mfa_api" ? "success" : "info"}>
    {PROTOCOL_LABEL[protocol] || protocol || "—"}
  </SquareBadge>
);

// The value an app is known by to its developers: the client ID (OIDC, MFA
// API) or the SAML entity ID.
export const appIdentifier = (app) => (app?.protocol === "saml" ? app?.saml?.entityId : app?.oidc?.clientId) || "";

// A confidential client: it has a secret that can be rotated.
export const hasRotatableSecret = (app) =>
  !!app && app.protocol !== "saml" && !!app.oidc?.hasSecret && app.oidc?.tokenEndpointAuthMethod !== "none";

export function AppLogo({ app, size = 28 }) {
  const [broken, setBroken] = React.useState(false);
  const box = {
    width: size,
    height: size,
    borderRadius: 7,
    flexShrink: 0,
    border: `1px solid ${AN_COLORS.line}`,
    background: AN_COLORS.bg,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  };
  if (app?.logoUrl && !broken) {
    return (
      <span style={box}>
        <img src={app.logoUrl} alt="" onError={() => setBroken(true)} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
      </span>
    );
  }
  return (
    <span style={{ ...box, color: AN_COLORS.muted }}>
      <AppWindow size={Math.round(size * 0.55)} />
    </span>
  );
}

/* ------------------------------------------------------------------ rules */
// The same checks as internal/idp (apps.go, samlapps.go, sources.go), so most
// mistakes are caught before the round trip. The API's 400 is still shown.

export const MAX_URIS = 20;
const MAX_URI_LEN = 2048;

const isLoopback = (host) => {
  const h = String(host || "").toLowerCase().replace(/^\[|\]$/g, "");
  return h === "localhost" || h === "::1" || /^127\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(h);
};

// Absolute https URL (http only for loopback: localhost, 127.x, ::1, as
// apps.go isLoopback), no wildcard, no fragment,
// no user info. Matched exactly by the server, so nothing is normalised here.
export const validRedirectUri = (raw) => {
  if (!raw || raw.length > MAX_URI_LEN || /[*\s]/.test(raw) || raw.includes("#")) return false;
  if (!/^https?:\/\/[^/]/i.test(raw)) return false;
  let u;
  try {
    u = new URL(raw);
  } catch {
    return false;
  }
  if (!u.hostname || u.username || u.password) return false;
  if (u.protocol === "https:") return true;
  return u.protocol === "http:" && isLoopback(u.hostname);
};

// The non-empty entries, trimmed and de-duplicated, as the server keeps them.
export const cleanUris = (list) => [...new Set((list || []).map((s) => String(s).trim()).filter(Boolean))];

// "" or the first problem with a list of redirect / ACS URIs.
export const uriListError = (list, { required, what = "redirect URI" } = {}) => {
  const uris = cleanUris(list);
  if (required && uris.length === 0) return `Add at least one ${what}.`;
  if (uris.length > MAX_URIS) return `At most ${MAX_URIS} ${what}s.`;
  const bad = uris.find((u) => !validRedirectUri(u));
  if (bad) return `${bad} is not allowed: use an absolute https:// URL (http only for localhost or 127.0.0.1), without * or #.`;
  return "";
};

export const validLogoUrl = (raw) => {
  if (!raw) return true;
  if (raw.length > MAX_URI_LEN || !/^https:\/\/[^/]/i.test(raw)) return false;
  try {
    return !!new URL(raw).hostname;
  } catch {
    return false;
  }
};

// A plain domain name (sources.go domainName).
export const validDomain = (d) =>
  d.length <= 253 && d.includes(".") && !d.startsWith(".") && !d.endsWith(".") && /^[a-z0-9.-]+$/.test(d);

export const normaliseDomain = (d) => String(d || "").trim().replace(/^@/, "").toLowerCase();

/* ---------------------------------------------------------------- errors */

// Handles a failed idp call the same way everywhere: 401 logs out, 403 says
// the administrator role is needed, 404 refreshes, 400 goes next to the form
// (setInline) when there is one, anything else is a toast.
export function reportIdpError(err, { msg, fallback, setInline, onNotFound, setForbidden } = {}) {
  const f = idpFailure(err, fallback);
  if (f.logout) {
    logOutUser();
  } else if (f.forbidden && setForbidden) {
    setForbidden(true);
  } else if (f.notFound && onNotFound) {
    msg?.warning(f.text);
    onNotFound();
  } else if (f.invalid && setInline) {
    setInline(f.text);
  } else {
    msg?.error(f.text);
  }
  return f;
}

export function Forbidden() {
  return (
    <div style={{ ...card, padding: 24, display: "flex", gap: 12, alignItems: "flex-start", maxWidth: "40rem" }}>
      <ShieldAlert size={20} color={AN_COLORS.muted} style={{ flexShrink: 0, marginTop: 2 }} />
      <div>
        <div style={{ fontWeight: 600, fontSize: 14, color: AN_COLORS.ink }}>Administrators only</div>
        <div style={{ fontSize: 13, color: AN_COLORS.muted, marginTop: 4 }}>
          You need the administrator role to manage applications. Ask an administrator of this tenant.
        </div>
      </div>
    </div>
  );
}

export const ErrorText = ({ children }) =>
  children ? <div style={{ fontSize: 12, color: AN_COLORS.danger, marginTop: 5 }}>{children}</div> : null;

export const HelpText = ({ children }) => (
  <div style={{ fontSize: 12, color: AN_COLORS.muted, marginTop: 5, lineHeight: 1.5 }}>{children}</div>
);

// Directories live in the Identity product. Switch to it, then open them.
export const openIdentitySources = (navigate) => {
  localStorage.setItem("currentMode", "mfa_ad");
  window.dispatchEvent(new Event("storage"));
  navigate("/pam/directory");
};

/* ------------------------------------------------------------- widgets */

// An editable list of URIs: one input per row, remove, add.
export function UriList({ value, onChange, placeholder, addLabel = "Add URI", status, inputStyle }) {
  const rows = value && value.length ? value : [""];
  const set = (i, v) => onChange(rows.map((r, j) => (j === i ? v : r)));
  const remove = (i) => onChange(rows.filter((_, j) => j !== i));
  return (
    <div style={{ display: "grid", gap: 6 }}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <Input
            value={r}
            placeholder={placeholder}
            spellCheck={false}
            status={status}
            onChange={(e) => set(i, e.target.value)}
            style={{ fontFamily: AN_MONO, fontSize: 12.5, flex: 1, minWidth: 0, ...inputStyle }}
          />
          <button
            type="button"
            aria-label="Remove"
            onClick={() => remove(i)}
            disabled={rows.length === 1 && !r}
            style={{
              width: 30,
              height: 30,
              borderRadius: 6,
              border: `1px solid ${AN_COLORS.line}`,
              background: AN_COLORS.surface,
              color: AN_COLORS.muted,
              cursor: rows.length === 1 && !r ? "not-allowed" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <X size={14} />
          </button>
        </div>
      ))}
      {rows.length < MAX_URIS && (
        <div>
          <Button size="small" icon={<Plus size={13} style={{ verticalAlign: -2 }} />} onClick={() => onChange([...rows, ""])}>
            {addLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

// One read-only value with a copy button: label, value, optional hint (under
// the label) and help (under the value).
export function ValueRow({ label, value, hint, help, first }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(8rem, 12rem) minmax(0, 1fr) auto",
        gap: 14,
        alignItems: "center",
        padding: "10px 0",
        borderTop: first ? "none" : `1px solid ${AN_COLORS.line2}`,
      }}
    >
      <div>
        <div style={{ fontSize: 12.5, fontWeight: 500, color: AN_COLORS.ink2 }}>{label}</div>
        {hint && <div style={{ fontSize: 11.5, color: AN_COLORS.muted, marginTop: 2 }}>{hint}</div>}
      </div>
      <div style={{ minWidth: 0 }}>
        <code style={{ fontFamily: AN_MONO, fontSize: 12.5, color: value ? AN_COLORS.ink : AN_COLORS.muted, wordBreak: "break-all" }}>
          {value || "—"}
        </code>
        {help && <HelpText>{help}</HelpText>}
      </div>
      {value ? <CopyButton text={value} /> : <span />}
    </div>
  );
}

/* --------------------------------------------------------------- secret */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "23 Oct 2026" (D MMM YYYY), or "" when there is no valid date.
export const formatDay = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

// What the console shows of a secret: its hint (ancs_ and four characters)
// followed by dots, or only dots when there is no hint.
export const maskedSecret = (hint) => `${hint || ""}${"\u2022".repeat(12)}`;

// The settings an app's developers need, as environment variables.
// AUTHNULL_URL is the tenant's address (the issuer without /api/v1/idp).
export const settingsBlock = ({ issuer, clientId, secret }) =>
  [`AUTHNULL_URL=${tenantBaseUrl(issuer)}`, `AUTHNULL_CLIENT_ID=${clientId || ""}`, `AUTHNULL_CLIENT_SECRET=${secret}`].join("\n");

// The secret's hint and when it was made. Never the secret itself.
export function SecretHint({ oidc }) {
  const created = formatDay(oidc?.secretCreatedAt);
  return (
    <div style={{ minWidth: 0 }}>
      <code style={{ fontFamily: AN_MONO, fontSize: 12.5, color: AN_COLORS.ink, wordBreak: "break-all" }}>
        {maskedSecret(oidc?.secretHint)}
      </code>
      {created && <div style={{ fontSize: 11.5, color: AN_COLORS.muted, marginTop: 2 }}>Created {created}</div>}
    </div>
  );
}

// Rotate secret: confirms, rotates, shows the new secret once, then calls
// onRotated (after the dialog is closed) so the app is read again.
export function RotateSecretButton({ app, issuer, onRotated, size }) {
  const [modal, modalCtx] = Modal.useModal();
  const [msg, msgCtx] = message.useMessage();
  // Only while the dialog is open; cleared when it closes.
  const [secret, setSecret] = useState(null);
  const rotate = () =>
    modal.confirm({
      title: "Rotate the client secret?",
      content: "The current secret stops working immediately. Apps using it will fail until updated.",
      okText: "Rotate secret",
      okButtonProps: { danger: true },
      onOk: () =>
        rotateAppSecret(app.id)
          .then((res) => setSecret(res?.clientSecret || null))
          .catch((err) => reportIdpError(err, { msg, fallback: "Could not rotate the client secret." })),
    });
  return (
    <>
      {modalCtx}
      {msgCtx}
      <Button size={size} icon={<RotateCcw size={size === "small" ? 13 : 14} style={{ verticalAlign: -2 }} />} onClick={rotate}>
        Rotate secret
      </Button>
      <SecretModal
        secret={secret}
        clientId={app?.oidc?.clientId}
        issuer={issuer}
        onDone={() => {
          setSecret(null);
          onRotated?.();
        }}
      />
    </>
  );
}

// The client secret, shown once, with the AuthNull URL and client ID: the
// three values the app needs, and "Copy all" as settings. The AuthNull URL is
// the tenant's address: the issuer without /api/v1/idp. This is the only
// moment the full secret exists. Closes only with "I've copied it": no X, no
// click outside, no Esc. The caller clears the secret in onDone, and never
// logs it. The issuer is read from the settings when the caller has none;
// if that fails, the rows that are known are shown.
export function SecretModal({ secret, clientId, issuer, onDone }) {
  const open = !!secret;
  const [loadedIssuer, setLoadedIssuer] = useState("");
  useEffect(() => {
    if (!open || issuer || loadedIssuer) return undefined;
    let alive = true;
    getIdpSettings()
      .then((res) => alive && setLoadedIssuer(res?.endpoints?.issuer || ""))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [open, issuer, loadedIssuer]);
  const url = tenantBaseUrl(issuer || loadedIssuer);
  const rows = [
    url && { label: "AuthNull URL", value: url },
    clientId && { label: "Client ID", value: clientId },
    { label: "Client secret", value: secret || "" },
  ].filter(Boolean);
  const all = [
    url && `AUTHNULL_URL=${url}`,
    clientId && `AUTHNULL_CLIENT_ID=${clientId}`,
    `AUTHNULL_CLIENT_SECRET=${secret || ""}`,
  ]
    .filter(Boolean)
    .join("\n");
  return (
    <Modal
      open={open}
      title="Client ID and secret"
      closable={false}
      maskClosable={false}
      keyboard={false}
      destroyOnClose
      width="40rem"
      footer={
        <Button type="primary" onClick={onDone}>
          I've copied it
        </Button>
      }
    >
      <div style={{ display: "grid", gap: 12, fontFamily: AN_FONT, paddingTop: 4 }}>
        <Note tone="warn" icon={<TriangleAlert size={15} />}>
          Copy the secret now. It is shown only once and cannot be retrieved later. If you lose it, rotate it.
        </Note>
        <div style={{ border: `1px solid ${AN_COLORS.line}`, borderRadius: 8, background: AN_COLORS.tint, padding: "0 12px" }}>
          {rows.map((r, i) => (
            <ValueRow key={r.label} first={i === 0} {...r} />
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: AN_COLORS.muted }}>
            Copy all as settings: AUTHNULL_URL, AUTHNULL_CLIENT_ID, AUTHNULL_CLIENT_SECRET.
          </span>
          <CopyButton text={all} label="Copy all" size="md" />
        </div>
      </div>
    </Modal>
  );
}
